import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Typography,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAccountAuth } from '../hooks/useAccountAuth';
import { OTP_LENGTH, RESEND_DELAY_S } from '../config/auth';
import { track } from '../utils/analytics';
import { getSessionUser } from '../lib/authAccount';
import {
  clearNewsletterOptIn,
  rememberNewsletterOptIn,
  subscribeToNewsletter,
} from '../lib/newsletter';

// Feuille d'inscription unifiee. Avant, s'inscrire voulait dire aller chercher
// un formulaire au fond des Settings : le seul moment ou l'inscription a du sens
// (on vient de debloquer quelque chose qu'on ne veut pas perdre) n'y menait pas.
// C'est le meme composant qui sert les deux points d'entree, pour qu'il n'y ait
// qu'une facon de se creer un compte.
//
// variant :
//  - 'beta'     : arrive juste apres un code d'invitation valide. L'acces est
//                 deja pose sur cet appareil, le compte sert a l'emporter.
//  - 'purchase' : arrive juste apres un achat. Meme logique, enjeu plus fort :
//                 sans compte, l'achat ne survit qu'au restore du store, donc
//                 pas sur une autre plateforme ni sur le web. Toujours
//                 facultatif : l'app est deja debloquee, on ne retient personne.
//  - 'before_purchase' : la seule qui PRECEDE. Sur le web, le verrou l'ouvre
//                 avant d'envoyer a la caisse (F3) : un achat fait en invite
//                 s'attache a la session anonyme du navigateur et n'en sort
//                 jamais. Le verrou reste ouvert derriere, cette feuille se
//                 retire, le bouton d'achat est la. Depuis S12 elle sert aussi
//                 la porte Patreon, qui posait l'acces sur le compte anonyme
//                 par le meme mecanisme : son texte parle donc d'acces et non
//                 d'achat, ce qui reste vrai des deux cotes (un achat EST un
//                 acces) et suit le wording neutre pose par S8.
//  - 'restore'  : arrive du verrou, quand l'utilisateur dit avoir deja achete.
//                 Ouvre directement en connexion : il n'y a pas de compte a
//                 creer, il y en a un a retrouver. C'est ce qui evite de faire
//                 payer deux fois quelqu'un qui a achete sur une autre
//                 plateforme, la ou "Restore purchases" ne peut rien sans
//                 identite sur le web.
//  - 'default'  : entree par les Settings.
//
// Le design suit UnlockSheet (meme fond, meme accent, meme rayon) : les deux
// feuilles se suivent souvent a une seconde d'intervalle.

const ACCENT = '#f97316';
const SURFACE = '#141420';

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

const AppleMark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
    <path d="M16.36 12.72c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.75 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.71.71 2.87.69 1.19-.02 1.94-1.08 2.66-2.14.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.34-3.51zM14.2 6.3c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.05 1.69-.92 2.69.97.07 1.96-.49 2.58-1.22z" />
  </svg>
);

// Bouton principal sur l'accent orange. Texte SOMBRE, pas blanc : blanc sur cet
// orange tombe a 2,8:1, sous le seuil AA et meme sous le 3:1 tolere au grand
// texte, alors que le sombre remonte a 7,5:1. Le hover garde la meme encre, un
// orange plus fonce n'ameliorerait pas du blanc.
const accentSx = {
  py: 1.3,
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  backgroundColor: ACCENT,
  color: '#0a0a0a',
  '&:hover': { backgroundColor: '#ea580c', color: '#0a0a0a' },
};

const outlinedSx = {
  py: 1.3,
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 500,
  color: 'white',
  borderColor: 'rgba(255,255,255,0.2)',
  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.06)' },
};

const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    color: 'white',
    fontSize: '1.15rem',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiOutlinedInput-input': {
    height: 'auto',
    padding: '18px 16px',
    lineHeight: 1.5,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '1.15rem',
    transform: 'translate(16px, 21px) scale(1)',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(15px, -9px) scale(0.75)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const SignUpSheet = ({ open, onClose, variant = 'default' }) => {
  const newsletterEligible = variant === 'default' || variant === 'beta';
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [newsletterSaving, setNewsletterSaving] = useState(false);
  const [newsletterError, setNewsletterError] = useState(null);

  const saveNewsletterOptIn = React.useCallback(async () => {
    if (!newsletterEligible || !newsletterOptIn) return;
    setNewsletterSaving(true);
    setNewsletterError(null);
    try {
      await subscribeToNewsletter();
      clearNewsletterOptIn('signup');
    } catch {
      const current = await getSessionUser();
      rememberNewsletterOptIn('retry', current?.id, 'retry');
      setNewsletterError('Your account is ready, but the newsletter signup is waiting for a connection.');
    } finally {
      setNewsletterSaving(false);
    }
  }, [newsletterEligible, newsletterOptIn]);

  const auth = useAccountAuth({ variant, onAccountAuthenticated: saveNewsletterOptIn });
  const {
    signedIn,
    mode,
    email,
    setEmail,
    code,
    setCode,
    codeSent,
    busy,
    error,
    notice,
    justSignedUp,
    pendingSignInEmail,
    startSignIn,
    resetForm,
    resetAll,
    sendCode,
    confirmSignInSwitch,
    cancelSignInSwitch,
    verifyCode,
    continueWithGoogle,
    continueWithApple,
    appleAvailable,
    user,
  } = auth;

  // Le choix "par email" est un etat d'ecran, pas une deduction sur le contenu
  // du champ : un utilisateur qui efface son email ne doit pas etre renvoye au
  // choix de depart.
  const [emailStep, setEmailStep] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  // Derniere valeur soumise toute seule : sans elle, un code refuse relancerait
  // la verification en boucle puisque le champ reste plein.
  const autoVerified = useRef(null);

  useEffect(() => {
    if (open) {
      track('signup_sheet_opened', { variant });
      if (variant === 'restore') startSignIn();
      // Pose a l'ouverture, pas en valeur initiale : resetAll le remet a faux a
      // chaque fermeture.
      if (variant === 'beta') setEmailStep(true);
    } else {
      clearNewsletterOptIn('signup');
      resetAll();
      setEmailStep(false);
      setNewsletterOptIn(false);
      setNewsletterError(null);
    }
  }, [open, variant, resetAll, startSignIn]);

  // Deja connecte et rien a confirmer : la feuille n'a plus rien a dire. On ne
  // la monte pas du tout (plutot que de la fermer dans un effet, qui la ferait
  // clignoter le temps d'une frame) et on previent le parent.
  //
  // Sauf pendant une operation en cours. Supabase annonce la nouvelle session des
  // qu'il l'a, c'est-a-dire au milieu de l'appel, avant que l'ecran de confirmation
  // soit arme : la feuille se jugeait alors inutile et se fermait sur un ecran vide.
  // Constate sur iPhone le 31/07/2026 avec Sign in with Apple, ou l'ecart est le plus
  // large, mais la creation par email suit le meme enchainement.
  const shouldShow = open && (!signedIn || justSignedUp || busy);

  useEffect(() => {
    if (open && signedIn && !justSignedUp && !busy) onClose();
  }, [open, signedIn, justSignedUp, busy, onClose]);

  const isBeta = variant === 'beta';
  const isPurchase = variant === 'purchase';
  const isRestore = variant === 'restore';
  // La seule variante qui PRECEDE ce qu'elle protege (spec v5 F3, S6) : sur le
  // web, le verrou l'ouvre avant d'envoyer a la caisse, parce qu'un achat fait
  // en invite reste dans ce navigateur et ne suit ni le telephone ni une autre
  // fenetre. La feuille de verrou reste ouverte derriere : quand celle-ci se
  // ferme, le bouton d'achat est la, pret.
  const isBeforePurchase = variant === 'before_purchase';

  useEffect(() => {
    if (mode !== 'create') {
      setNewsletterOptIn(false);
      clearNewsletterOptIn('signup');
    }
  }, [mode]);
  // Apres un code d'invitation, l'adresse n'est pas un detail : le code est
  // arrive par mail, et c'est cette adresse-la qui doit porter le compte pour
  // que l'acces se retrouve sur le telephone. Un bouton tiers a cet endroit
  // ouvre un compte a une autre adresse, celle de l'appareil, et l'acces reste
  // derriere. La guideline 4.8 n'impose Sign in with Apple que si un login
  // tiers est propose : n'en proposer aucun est conforme.
  const emailOnly = isBeta;
  // Les deux arrivent apres un deverrouillage : meme structure, l'un parle
  // d'invitation, l'autre d'achat.
  const afterUnlock = isBeta || isPurchase;
  const step = justSignedUp
    ? 'done'
    : pendingSignInEmail
      ? 'existing-account'
      : codeSent
        ? 'code'
        : emailStep
          ? 'email'
          : 'choice';

  // Le compte a rebours part quand le code est envoye et s'arrete a zero. Meme
  // cadence que la page d'invitation, c'est celle de Supabase (config/auth.js).
  useEffect(() => {
    setResendIn(codeSent ? RESEND_DELAY_S : 0);
  }, [codeSent]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Le code est complet : rien n'attend plus que le doigt de l'utilisateur, et un
  // bouton de plus a ce moment-la coute un geste sur les cinq du parcours. Le
  // bouton reste affiche pour la reprise apres un refus.
  useEffect(() => {
    if (step !== 'code' || busy) return;
    const value = code.trim();
    if (value.length !== OTP_LENGTH || autoVerified.current === value) return;
    autoVerified.current = value;
    verifyCode();
  }, [step, busy, code, verifyCode]);

  const handleEmailPath = () => {
    track('signup_email_started');
    setEmailStep(true);
  };

  const handleGoogle = () => {
    if (newsletterEligible && newsletterOptIn && mode === 'create') rememberNewsletterOptIn('signup', user?.id);
    else clearNewsletterOptIn('signup');
    continueWithGoogle();
  };

  const handleApple = async () => {
    await continueWithApple();
    if (!justSignedUp) clearNewsletterOptIn('signup');
  };

  const backToChoice = () => {
    resetForm();
    setEmail('');
    setEmailStep(false);
  };

  // Le compte a rebours repart avant l'envoi : meme si le serveur refuse (sa
  // propre cadence), le bouton ne doit pas rester cliquable en rafale.
  //
  // Le champ se vide au passage. Demander un autre code veut dire que celui
  // affiche ne vaut plus : le laisser en place le faisait repartir tout seul
  // sous l'auto-verification, une tentative brulee sur un code deja refuse,
  // avant meme que le nouveau mail arrive. Vider le champ ET rouvrir
  // l'auto-verification : le champ vide ne declenche rien, et le code suivant
  // part seul meme si le serveur renvoie le meme.
  const resendCode = () => {
    setResendIn(RESEND_DELAY_S);
    setCode('');
    autoVerified.current = null;
    sendCode();
  };

  const body = () => {
    if (justSignedUp) {
      return (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4ade80', mb: 1 }} />
          <Typography sx={{ color: 'white', fontSize: '1.25rem', fontWeight: 500, mb: 1 }}>
            You're all set.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.5, mb: 3 }}>
            {isPurchase
              ? 'Your purchase now follows your account. Sign in anywhere and it comes with you.'
              : isBeforePurchase
                ? 'Your account is ready. Your access will follow it from now on.'
                : isBeta
                  ? 'Your access now follows your account. Sign in on your phone and it comes with you.'
                  : isRestore
                    ? "You're back in. Everything came back with your account."
                    : 'Your settings now follow your account on every device.'}
          </Typography>
          {newsletterSaving && <CircularProgress size={20} sx={{ color: ACCENT, mb: 2 }} />}
          {newsletterError && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#fbbf24', fontSize: '0.9rem', lineHeight: 1.5, mb: 1 }}>
                {newsletterError}
              </Typography>
              <Button variant="outlined" onClick={saveNewsletterOptIn} sx={outlinedSx}>Try newsletter signup again</Button>
            </Box>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={accentSx}
          >
            {/* Ce qu'on allait faire attend derriere, la feuille de verrou ne
                s'est jamais fermee : on y renvoie, plutot qu'au manche. Le
                libelle ne nomme pas l'acte, il vaut pour l'achat comme pour
                Patreon. */}
            {isBeforePurchase ? 'Continue' : 'Back to playing'}
          </Button>
        </Box>
      );
    }

    // C2. L'adresse porte déjà un compte et un achat est en jeu. Ailleurs la
    // feuille bascule en connexion sans rien dire, ce qui est rendre service ;
    // ici cela purge les données locales et laisse l'achat sur l'identité
    // anonyme quittée. La bascule attend donc un geste, pas un silence.
    if (step === 'existing-account') {
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <DevicesIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }} />
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              Before you sign in
            </Typography>
          </Box>

          <Typography sx={{ color: 'white', fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
            This email already has an account
          </Typography>

          <Typography
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.5, mb: 1.5 }}
          >
            {pendingSignInEmail} already has an upScale account. Signing in loads that account
            on this device and replaces the session you are using right now.
          </Typography>

          {/* Deuxième phrase séparée : le Restore n'existe que sur le
              téléphone, et before_purchase s'ouvre sur le web, avant tout
              paiement. Lui promettre un bouton absent serait pire que le
              silence qu'on vient de retirer. */}
          <Typography
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.5, mb: 2.5 }}
          >
            {isBeforePurchase
              ? 'Anything you did as a guest on this device is replaced by that account.'
              : 'Your purchase stays with this device: once you are signed in, tap Restore purchases to bring it back.'}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            disabled={busy}
            onClick={confirmSignInSwitch}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={accentSx}
          >
            Sign in to that account
          </Button>

          <Button
            fullWidth
            disabled={busy}
            startIcon={<ArrowBackIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={cancelSignInSwitch}
            sx={{
              mt: 0.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              color: 'rgba(255,255,255,0.65)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Use a different email
          </Button>

          {error && (
            <Typography
              sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.9rem', color: '#f87171' }}
            >
              {error}
            </Typography>
          )}
        </>
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <DevicesIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }} />
          <Typography
            sx={{
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.55)',
            }}
          >
            {isRestore
              ? 'Get it back'
              : mode === 'signin'
                ? 'Sign in'
                : isBeforePurchase
                  ? 'First, your account'
                  : 'Keep your access'}
          </Typography>
        </Box>

        <Typography sx={{ color: 'white', fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
          {isRestore
            ? 'Your access is on your account'
            : mode === 'signin'
              ? 'Welcome back'
              : isPurchase
                ? 'Secure your purchase'
                : isBeforePurchase
                  ? 'Your account holds your access'
                  : isBeta
                    ? "You're in. Now make it yours."
                    : 'Take it everywhere'}
        </Typography>

        <Typography
          sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.5, mb: 2.5 }}
        >
          {isRestore
            ? 'Sign in with the account you used to activate your access, and everything comes back. Anything you did as a guest in this browser is replaced by your account.'
            : mode === 'signin'
              ? 'Sign in to load your account on this device. Anything you did as a guest here is replaced by your account.'
              : isPurchase
                ? 'The app is unlocked on this device. Create an account and your purchase follows you to your phone, your tablet, and any browser you sign in from.'
                : isBeforePurchase
                  ? 'Sign in or create an account now, and your access attaches to it: it follows you to your phone, your tablet, and any browser you sign in from. Without one, it would stay in this browser.'
                  : isBeta
                    ? 'Your invitation is active on this device. Create an account and it follows you to your phone, your tablet, and any browser you sign in from.'
                    : 'Create an account to keep your settings and everything you unlocked on all your devices.'}
        </Typography>

        {newsletterEligible && mode === 'create' && (step === 'choice' || step === 'email' || step === 'code') && (
          <FormControlLabel
            control={
              <Checkbox
                checked={newsletterOptIn}
                onChange={(event) => setNewsletterOptIn(event.target.checked)}
                disabled={busy || codeSent}
                inputProps={{ 'aria-label': 'Newsletter consent' }}
                sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: ACCENT } }}
              />
            }
            label="Email me the upScale newsletter with guitar tips, new features and news."
            sx={{ alignItems: 'flex-start', mb: 2, color: 'rgba(255,255,255,0.78)', '& .MuiFormControlLabel-label': { fontSize: '0.9rem', lineHeight: 1.45, pt: '7px' } }}
          />
        )}

        {step === 'choice' && (
          <>
            {/* L'email en premier, parce que c'est par la que l'acces arrive :
                une invitation, un achat, tout part d'une adresse. Le bouton
                d'un tiers en tete se tape par reflexe, surtout celui de l'iPhone
                qu'on tient, et ouvre un compte a une AUTRE adresse : vide, sans
                l'acces qu'on vient de deverrouiller ailleurs.
                Guideline 4.8 : elle porte sur les logins TIERS entre eux, Apple
                devant Google. L'email n'en est pas un, le passer devant ne la
                concerne pas. */}
            <Button
              fullWidth
              variant="outlined"
              disabled={busy}
              startIcon={<MailOutlineIcon />}
              onClick={handleEmailPath}
              sx={{ ...outlinedSx, mb: !isRestore && !isPurchase && !isBeforePurchase ? 1 : 2 }}
            >
              Continue with email
            </Button>

            {/* Inutile sur restore, purchase et before_purchase : leur propre
                texte dit deja quelle adresse est attendue, ou qu'aucune ne
                l'est. Rien n'a ete deverrouille par un mail avant un achat.
                Un vrai bouton, pas un rappel (09/09/2026, Christophe) : la
                feuille ne parlait que de creer un compte, et quelqu'un qui en a
                deja un passait par le rattachement, refuse, puis le repli, soit
                deux selecteurs Google pour un geste. En mode connexion, Google
                part directement en connexion pure. */}
            {!isRestore && !isPurchase && !isBeforePurchase && mode !== 'signin' && (
              <Button
                fullWidth
                disabled={busy}
                onClick={startSignIn}
                sx={{
                  mb: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  color: 'rgba(255,255,255,0.65)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Already have an account? Sign in
              </Button>
            )}

            {appleAvailable && (
              <Button
                fullWidth
                variant="outlined"
                disabled={busy}
                startIcon={<AppleMark />}
                onClick={handleApple}
                sx={{ ...outlinedSx, mb: 1.5 }}
              >
                Continue with Apple
              </Button>
            )}

            <Button
              fullWidth
              variant="outlined"
              disabled={busy}
              startIcon={<GoogleMark />}
              onClick={handleGoogle}
              sx={outlinedSx}
            >
              Continue with Google
            </Button>

            <Typography
              sx={{
                mt: 2,
                textAlign: 'center',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              No password. We send you a six-digit code.
            </Typography>
          </>
        )}

        {(step === 'email' || step === 'code') && (
          <>
            <TextField
              fullWidth
              autoFocus={step === 'email'}
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !codeSent) sendCode();
              }}
              autoComplete="email"
              disabled={busy || codeSent}
              sx={fieldSx}
            />

            {step === 'code' && (
              <TextField
                fullWidth
                autoFocus
                label="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') verifyCode();
                }}
                autoComplete="one-time-code"
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                disabled={busy}
                sx={fieldSx}
              />
            )}

            {emailOnly && !codeSent && (
              <Typography
                sx={{
                  mt: -1,
                  mb: 2,
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  color: 'rgba(255,255,255,0.55)',
                }}
              >
                Use the address your invitation was sent to. If a friend shared their code,
                any address works, just pick one you'll remember.
              </Typography>
            )}

            {/* Dit ce qui va se passer AVANT que le mail parte : sans cette
                ligne, la personne decouvre au retour de sa boite qu'elle devait
                surveiller cette adresse-la. */}
            {!codeSent && (
              <Typography
                sx={{
                  mt: -1,
                  mb: 2,
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  color: 'rgba(255,255,255,0.55)',
                }}
              >
                We'll email you a 6-digit code. Check the inbox of that address.
              </Typography>
            )}

            {codeSent && (
              <Box
                sx={{
                  mt: -1,
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                  Nothing yet? Check your spam folder.
                </Typography>
                <Button
                  disabled={busy || resendIn > 0}
                  onClick={resendCode}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: ACCENT,
                    minWidth: 0,
                    px: 1,
                    flexShrink: 0,
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                    '&:hover': { backgroundColor: 'rgba(249,115,22,0.08)' },
                  }}
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend verification code'}
                </Button>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              disabled={busy}
              onClick={codeSent ? verifyCode : sendCode}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
              sx={accentSx}
            >
              {codeSent
                ? mode === 'create'
                  ? 'Create account'
                  : 'Sign in'
                : 'Send verification code'}
            </Button>

            {/* Pas de retour vers un ecran de choix qui n'a pas ete affiche :
                en mode email seul, il n'y a rien a proposer d'autre. */}
            {(codeSent || !emailOnly) && (
              <Button
                fullWidth
                disabled={busy}
                startIcon={<ArrowBackIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={codeSent ? resetForm : backToChoice}
                sx={{
                  mt: 0.5,
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.65)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                }}
              >
                {codeSent ? 'Use a different email' : 'Other ways to sign in'}
              </Button>
            )}
          </>
        )}

        {(error || notice) && (
          <Typography
            sx={{
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.9rem',
              color: error ? '#f87171' : '#4ade80',
            }}
          >
            {error || notice}
          </Typography>
        )}

        {/* Plus de "j'ai deja un compte / je dois en creer un" : c'est le serveur
            qui tranche. L'adresse suffit, sendCode bascule tout seul en connexion
            quand elle porte deja un compte. Une question de moins a laquelle
            l'utilisateur n'avait pas la reponse.
            Sauf depuis 'purchase' et 'before_purchase' (C2) : la bascule y
            purgerait les données locales et laisserait l'achat derrière, elle
            passe donc par l'écran d'avertissement ci-dessus. */}

        {/* Jamais desactive : c'est la seule sortie de la feuille. Quand il
            dependait de `busy`, un login Google qui ne revenait pas laissait
            l'utilisateur enferme ici jusqu'au redemarrage de l'app. */}
        <Button
          fullWidth
          onClick={onClose}
          sx={{
            mt: 1,
            textTransform: 'none',
            fontSize: '0.9rem',
            color: 'rgba(255,255,255,0.5)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
          }}
        >
          {afterUnlock ? 'Later, let me play' : 'Not now'}
        </Button>
      </>
    );
  };

  return (
    <Drawer
      anchor="bottom"
      open={shouldShow}
      // Toujours fermable, meme pendant une operation : une feuille qu'on ne peut
      // plus quitter transforme le moindre retour d'auth manquant en app figee.
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          backgroundColor: SURFACE,
          backgroundImage: 'none',
          px: 3,
          pt: 2.5,
          pb: 'calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
          maxWidth: '520px',
          mx: 'auto',
        },
      }}
    >
      {body()}
    </Drawer>
  );
};

export default SignUpSheet;
